<template>
  <modal
    v-if="visible"
    :visible="visible"
    @click-outside-modal="$emit('close')"
  >
    <div class="flex justify-center px-4">
      <div
        class="flex w-[508px] flex-col items-start gap-5 rounded bg-white p-8 text-left"
      >
        <div class="flex w-full flex-row items-center justify-between">
          <h3 class="text-base font-medium text-neutral-boldest">
            Edit {{ capitalizeFirstLetter(contact?.is_type) }} Contact
          </h3>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            @click="$emit('close')"
          >
            <path
              d="M18.3642 7.05026L16.9499 5.63605L12.0002 10.5858L7.05044 5.63605L5.63623 7.05026L10.586 12L5.63623 16.9498L7.05044 18.364L12.0002 13.4142L16.9499 18.364L18.3642 16.9498L13.4144 12L18.3642 7.05026Z"
              fill="#6C7C9D"
            />
          </svg>
        </div>

        <div class="w-full text-left">
          <AutocompleteContacts
            v-model="name"
            label="Contact Name"
            placeholder="Type or select a contact name"
            :options="contactNameOptions"
            :min-chars="1"
            @selected="handleNameSearch"
          />
          <div
            v-if="nameError"
            class="mt-1 text-xs text-red-500"
          >
            {{ nameError }}
          </div>
        </div>
        <div class="w-full text-left">
          <AutocompletePhone
            v-model="phone"
            :options="contactPhoneOptions"
            :phone-e164="contact?.phone?.phone_e164"
            create-label="Add Phone"
            label="Phone Number"
            placeholder="Enter phone number"
            @selected="handlePhoneSearch"
          />
          <div
            v-if="phoneError"
            class="mt-1 text-xs text-red-500"
          >
            {{ phoneError }}
          </div>
        </div>

        <div class="w-full text-left">
          <AutocompleteContacts
            v-model="email"
            label="Email"
            placeholder="Type or select an email"
            :options="contactEmailOptions"
            create-label="Add Email"
            :min-chars="1"
            @selected="handleEmailSearch"
          />
          <div
            v-if="emailError"
            class="mt-1 text-xs text-red-500"
          >
            {{ emailError }}
          </div>
        </div>
        <div
          class="mt-4 flex w-full flex-row items-center justify-center gap-3"
        >
          <button
            class="h-10 w-full rounded-md bg-steel-40 px-3 text-sm font-medium text-neutral-subtle-bold hover:bg-hovered-gray active:bg-pressed-gray"
            @click="$emit('close')"
          >
            Not Now
          </button>
          <button
            class="h-10 w-full rounded-md bg-neutral-subtle-yellow px-3 text-sm font-medium text-neutral-boldest hover:bg-neutral-subtle-yellow-bold active:bg-neutral-subtle-yellow-boldest disabled:cursor-not-allowed disabled:border-light-gray disabled:bg-light-gray disabled:text-pressed-gray"
            :disabled="!isFormValid || isLoading"
            :isLoading="isLoading"
            @click="submitRequest"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import AutocompleteContacts from '@/components/AutocompleteContacts.vue';
import AutocompletePhone from '@/components/AutocompletePhone.vue';
import Modal from '@/components/modals/Modal.vue';
import WordTools from '@/mixins/wordTools';
import { formatPhone, isEmail, fullnameValidation } from '@/common/validators';

export default {
  name: 'UpdateInsuranceModal',
  components: {
    Modal,
    AutocompleteContacts,
    AutocompletePhone,
  },
  mixins: [WordTools],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
    contact: {
      type: Object,
      default: () => ({}),
    },
    contacts: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      isLoading: false,
      name: '',
      email: '',
      phone: '',
      iso: '',
      initialized: false,
      nameError: '',
      emailError: '',
      phoneError: '',
    };
  },
  mounted() {
    this.name = this.contact?.name ?? '';
    this.email = this.contact?.email_address?.value ?? '';
    this.phone = this.contact?.phone?.value ?? '';

    setTimeout(() => {
      this.initialized = true;
    }, 100);
  },
  computed: {
    contactNameOptions() {
      const contactArray = Object.values(this.contacts ?? {}).filter(Boolean);
      const seen = new Set();
      return contactArray
        .filter((contact) => {
          if (seen.has(contact.name)) return false;
          seen.add(contact.name);
          return true;
        })
        .map((contact) => ({
          description: contact.name,
        }));
    },
    contactEmailOptions() {
      const contactArray = Object.values(this.contacts ?? {}).filter(Boolean);
      const seen = new Set();
      return contactArray
        .filter((contact) => {
          if (seen.has(contact.email_address.value)) return false;
          seen.add(contact.email_address.value);
          return true;
        })
        .map((contact) => ({
          description: contact.email_address.value,
        }));
    },
    contactPhoneOptions() {
      const contactArray = Object.values(this.contacts ?? {}).filter(Boolean);
      const seen = new Set();
      return contactArray
        .filter((contact) => {
          if (seen.has(contact.phone.phone_e164)) return false;
          seen.add(contact.phone.phone_e164);
          return true;
        })
        .map((contact) => ({
          description: contact.phone.phone_e164,
        }));
    },
    errorsExist() {
      return this.nameError || this.emailError || this.phoneError;
    },
    isFormValid() {
      return this.name && this.email && this.phone && !this.errorsExist;
    },
  },
  methods: {
    validateName() {
      const validation = fullnameValidation(this.name);
      this.nameError = validation === true ? '' : validation;
      return validation === true;
    },
    validateEmail() {
      if (!this.email) {
        this.emailError = 'Email is required.';
        return false;
      }
      if (!isEmail(this.email)) {
        this.emailError = 'Please enter a valid email address.';
        return false;
      }
      this.emailError = '';
      return true;
    },
    validatePhone() {
      if (!this.phone) {
        this.phoneError = 'Phone number is required.';
        return false;
      }
      // Basic phone validation - at least 10 digits
      const phoneDigits = this.phone.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        this.phoneError = 'Please enter a valid phone number.';
        return false;
      }
      this.phoneError = '';
      return true;
    },
    validateForm() {
      const isNameValid = this.validateName();
      const isEmailValid = this.validateEmail();
      const isPhoneValid = this.validatePhone();
      return isNameValid && isEmailValid && isPhoneValid;
    },
    handleEmailSearch(query) {
      if (!this.initialized) return;

      this.email =
        typeof query === 'string' ? query : (query?.description ?? '');
      this.validateEmail();
    },
    handleNameSearch(query) {
      if (!this.initialized) return;
      this.name =
        typeof query === 'string' ? query : (query?.description ?? '');
      this.validateName();
    },
    handlePhoneSearch(query) {
      if (!this.initialized) return;
      this.phone =
        typeof query.phone === 'string'
          ? query.phone
          : (query?.phone?.description ?? '');
      this.iso = query.iso;
      this.validatePhone();
    },
    onInput(e) {
      const { value } = e.target;
      let val = value;
      const formattedValue = formatPhone(value);
      val = formattedValue;
      this.phone = val;
    },
    submitRequest() {
      if (!this.validateForm()) {
        return;
      }

      this.$emit('submit', {
        name: this.name,
        email: this.email,
        phone: this.phone,
        iso: this.iso,
        type: this.type,
        existing_contact: this.contact,
      });
    },
    capitalizeFirstLetter(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
  },
};
</script>

<style scoped></style>
